// src/services/api.service.js
import axios from 'axios';
import store from '../store';
import router from '../router';

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000
});

apiClient.interceptors.request.use(
  config => {
    const token = store.getters['auth/token'];
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  error => Promise.reject(error)
);

apiClient.interceptors.response.use(
  response => response,
  error => {
    // 🔴 Les routes d'authentification sont exclues du renvoi automatique vers /login.
    // Pendant la connexion en deux temps, un code à six chiffres erroné répond 401 : sans
    // cette exception, la page serait rechargée et le jeton de passage perdu — l'utilisateur
    // repartirait du mot de passe à chaque faute de frappe, sans jamais voir le message
    // d'erreur qui lui dit ce qui cloche.
    const url = error.config?.url || '';
    const estAuth = url.startsWith('/auth/') || url.includes('/auth/');
    if (error.response && error.response.status === 401 && !estAuth) {
      store.dispatch('auth/logout');
      router.push('/login');
    }
    // Compte en cours de suppression (B9) : seule la page de suppression a encore un sens.
    if (error.response && error.response.status === 403 && error.response.data?.code === 'COMPTE_EN_SUPPRESSION'
        && router.currentRoute.value.path !== '/compte/suppression') {
      router.push('/compte/suppression');
    }
    return Promise.reject(error);
  }
);

export default {
  // ---- Auth ----
  login(credentials) {
    return apiClient.post('/auth/admin/login', credentials);
  },
  loginClient(credentials) {
    return apiClient.post('/auth/client/login', credentials);
  },
  // Inscription publique : destination du QR du guide de démarrage. La réponse est
  // volontairement identique pour une adresse libre ou déjà prise.
  register(payload) {
    return apiClient.post('/auth/register', payload);
  },
  changePassword(passwords) {
    return apiClient.post('/auth/admin/change-password', passwords);
  },
  changeClientPassword(passwords) {
    return apiClient.post('/auth/client/change-password', passwords);
  },
  // `userType` accompagne l'adresse : administrateurs et clients vivent dans deux tables
  // distinctes, et la même adresse pourrait théoriquement porter un compte de chaque type.
  forgotPassword(email, userType = 'client') {
    return apiClient.post('/auth/forgot-password', { email, userType });
  },
  resetPassword(token, newPassword) {
    return apiClient.post('/auth/reset-password', { token, newPassword });
  },

  // ---- Second facteur (connexion en deux temps) ----
  //
  // Ces appels portent le jeton de PASSAGE dans le corps, jamais dans l'en-tête : il n'ouvre
  // pas l'API, il ne fait que transporter l'identité d'une étape à l'autre.
  mfaVerifier(payload) {
    return apiClient.post('/auth/mfa/verify', payload);
  },
  mfaEnrolementDemarrer(payload) {
    return apiClient.post('/auth/mfa/enroll/start', payload);
  },
  mfaEnrolementConfirmer(payload) {
    return apiClient.post('/auth/mfa/enroll/confirm', payload);
  },
  // « Je n'ai rien reçu » : vaut pour la connexion comme pour l'enrôlement.
  mfaRenvoyerCode(mfaToken, usage = 'login') {
    return apiClient.post('/auth/mfa/email/resend', { mfaToken, usage });
  },
  mfaRecuperationDemander(mfaToken) {
    return apiClient.post('/auth/mfa/recovery/request', { mfaToken });
  },

  // ---- Sécurité du compte connecté ----
  securiteEtat() {
    return apiClient.get('/auth/me/security');
  },
  // Compte en mode courriel : recevoir un code depuis le profil (régénération des codes).
  envoyerCodeSession() {
    return apiClient.post('/auth/me/mfa/email/send');
  },
  regenererCodesSecours(code) {
    return apiClient.post('/auth/me/mfa/backup-codes', { code });
  },
  definirMonAdresse(email, password) {
    return apiClient.put('/auth/me/email', { email, password });
  },
  listerAppareilsDeConfiance() {
    return apiClient.get('/auth/me/devices');
  },
  oublierAppareil(id) {
    return apiClient.delete(`/auth/me/devices/${id}`);
  },
  // Sessions de l'app mobile (B5) : `id` = numéro, ou 'autres' (toutes sauf celle qui demande).
  sessionsMobiles() {
    return apiClient.get('/auth/me/sessions');
  },
  fermerSessionMobile(id) {
    return apiClient.delete(`/auth/me/sessions/${id}`);
  },
  // Suppression du compte (B9).
  suppressionEtat() {
    return apiClient.get('/auth/me/deletion');
  },
  suppressionDemander(password) {
    return apiClient.post('/auth/me/deletion', { password });
  },
  suppressionAnnuler() {
    return apiClient.post('/auth/me/deletion/cancel');
  },
  journalConnexions() {
    return apiClient.get('/auth/me/logins');
  },

  // ---- Comptes administrateurs ----
  listerAdmins() {
    return apiClient.get('/admins');
  },
  inviterAdmin(payload) {
    return apiClient.post('/admins/invite', payload);
  },
  revoquerInvitation(id) {
    return apiClient.delete(`/admins/invitations/${id}`);
  },
  definirAdminActif(id, active) {
    return apiClient.patch(`/admins/${id}/active`, { active });
  },
  supprimerAdmin(id) {
    return apiClient.delete(`/admins/${id}`);
  },
  reinitialiserMfa(type, id) {
    return apiClient.post(`/admins/mfa/reset/${type}/${id}`);
  },
  // Comptes partagés : invitation d'un membre (publique), et gestion des membres (titulaire).
  lireInvitationMembre(token) {
    return apiClient.get(`/auth/member-invitation/${token}`);
  },
  accepterInvitationMembre(token, password) {
    return apiClient.post(`/auth/member-invitation/${token}/accept`, { password });
  },
  listerMembresClient(clientId) {
    return apiClient.get(`/admin/clients/${clientId}/members`);
  },
  retirerMembreClient(clientId, id) {
    return apiClient.delete(`/admin/clients/${clientId}/members/${id}`);
  },
  listerMembres() {
    return apiClient.get('/clients/me/members');
  },
  inviterMembre(payload) {
    return apiClient.post('/clients/me/members/invite', payload);
  },
  modifierMembre(id, payload) {
    return apiClient.patch(`/clients/me/members/${id}`, payload);
  },
  retirerMembre(id) {
    return apiClient.delete(`/clients/me/members/${id}`);
  },
  annulerInvitationMembre(id) {
    return apiClient.delete(`/clients/me/member-invitations/${id}`);
  },
  lireInvitation(token) {
    return apiClient.get(`/auth/invitation/${token}`);
  },
  accepterInvitation(token, password) {
    return apiClient.post(`/auth/invitation/${token}/accept`, { password });
  },

  // ---- Admin ----
  getAdminDashboard() {
    return apiClient.get('/admin/dashboard');
  },
  getClients() {
    return apiClient.get('/admin/clients');
  },
  searchClients(query) {
    return apiClient.get(`/admin/clients?search=${query}`);
  },
  // Suppression de compte demandée par le client (B9) : l'admin l'annule à sa demande.
  annulerSuppressionClient(clientId) {
    return apiClient.post(`/admin/clients/${clientId}/deletion/cancel`);
  },
  getClientDetails(clientId) {
    return apiClient.get(`/admin/clients/${clientId}`);
  },
  createClient(clientData) {
    return apiClient.post('/admin/clients', clientData);
  },
  updateClient(clientId, clientData) {
    return apiClient.put(`/admin/clients/${clientId}`, clientData);
  },
  toggleClientStatus(clientId, active) {
    return apiClient.patch(`/admin/clients/${clientId}/status`, { active });
  },
  deleteClient(clientId) {
    return apiClient.delete(`/admin/clients/${clientId}`);
  },
  getAllEvents() {
    return apiClient.get('/admin/events');
  },
  // Recherche dans l'historique complet. `params` : q, clientId, deviceId, types, from, to,
  // limit, offset (voir backend/utils/event-query.js).
  searchAdminEvents(params) {
    return apiClient.get('/admin/events/search', { params });
  },
  getAdminEventFacets() {
    return apiClient.get('/admin/events/facets');
  },

  // Cartes provisionnées en atelier, pas encore rattachées à un client
  getUnassignedDevices() {
    return apiClient.get('/admin/unassigned-devices');
  },
  attachDevice(imei, payload) {
    return apiClient.post(`/admin/unassigned-devices/${imei}/attach`, payload);
  },
  // Émet le code de livraison. La réponse porte le code EN CLAIR et le QR : c'est la seule
  // fois où le serveur peut les produire (le code n'est conservé qu'en empreinte SHA-256).
  getSignupQr() {
    return apiClient.get('/admin/signup-qr');
  },
  issueClaimCode(imei, payload) {
    return apiClient.post(`/admin/unassigned-devices/${imei}/claim-code`, payload || {});
  },
  sendClaimCode(imei, code, email) {
    return apiClient.post(`/admin/unassigned-devices/${imei}/claim-code/send`, { code, email });
  },
  // Rattachement par le client : le code fait autorité, pas l'adresse courriel.
  claimDevice(payload) {
    // `/clients` au PLURIEL : c'est le préfixe de montage de client.routes dans app.js. Le
    // singulier renvoyait un 404 que rien ne signalait côté interface.
    return apiClient.post('/clients/claim', payload);
  },

  // ---- Devices ----
  getAllDevices(status) {
    return apiClient.get('/devices', { params: status ? { status } : {} });
  },
  getDevicesByClient(clientId) {
    return apiClient.get(`/devices/client/${clientId}`);
  },
  getDeviceDetails(deviceId) {
    return apiClient.get(`/devices/${deviceId}`);
  },
  createDevice(deviceData) {
    return apiClient.post('/devices', deviceData);
  },
  updateDevice(deviceId, deviceData) {
    return apiClient.put(`/devices/${deviceId}`, deviceData);
  },
  deleteDevice(deviceId) {
    return apiClient.delete(`/devices/${deviceId}`);
  },
  getDeviceEvents(deviceId, limit = 50) {
    return apiClient.get(`/devices/${deviceId}/events?limit=${limit}`);
  },
  getDeviceBattery(deviceId, days = 30) {
    return apiClient.get(`/devices/${deviceId}/battery?days=${days}`);
  },
  getDeviceBatterySamples(deviceId, fromTs, toTs) {
    return apiClient.get(`/devices/${deviceId}/battery/samples?from=${fromTs}&to=${toTs}`);
  },
  resetDeviceBattery(deviceId) {
    return apiClient.delete(`/devices/${deviceId}/battery`);
  },

  // ---- Contacts ----
  getDeviceContacts(deviceId) {
    return apiClient.get(`/devices/${deviceId}/contacts`);
  },
  createContact(deviceId, contactData) {
    return apiClient.post(`/devices/${deviceId}/contacts`, contactData);
  },
  updateContact(deviceId, contactId, contactData) {
    return apiClient.put(`/devices/${deviceId}/contacts/${contactId}`, contactData);
  },
  deleteContact(deviceId, contactId) {
    return apiClient.delete(`/devices/${deviceId}/contacts/${contactId}`);
  },

  // Multilingue : langue du compte (e-mails, alertes des contacts sans langue).
  definirLangue(lang) {
    return apiClient.put('/auth/me/langue', { lang });
  },
  // ---- Accessories ----
  getDeviceAccessories(deviceId) {
    return apiClient.get(`/devices/${deviceId}/accessories`);
  },
  updateAccessoryName(deviceId, accId, name) {
    return apiClient.put(`/devices/${deviceId}/accessories/${accId}`, { name });
  },
  deleteAccessory(deviceId, accId) {
    return apiClient.delete(`/devices/${deviceId}/accessories/${accId}`);
  },
  deleteAllAccessories(deviceId) {
    return apiClient.delete(`/devices/${deviceId}/accessories`);
  },

  // ---- Commandes MQTT (POST /devices/:id/cmd) ----
  // Le payload est { command, ...data }
  sendCommand(deviceId, command, data) {
    const payload = data !== undefined ? { command, ...data } : { command };
    return apiClient.post(`/devices/${deviceId}/cmd`, payload);
  },

  // ---- Schedule ----
  getSchedule(deviceId) {
    return apiClient.get(`/devices/${deviceId}/schedule`);
  },
  updateScheduleConfig(deviceId, data) {
    return apiClient.put(`/devices/${deviceId}/schedule/config`, data);
  },
  setScheduleSlot(deviceId, slotData) {
    return apiClient.post(`/devices/${deviceId}/schedule/slots`, slotData);
  },
  patchScheduleSlot(deviceId, slotId, data) {
    return apiClient.patch(`/devices/${deviceId}/schedule/slots/${slotId}`, data);
  },
  deleteScheduleSlot(deviceId, slotId) {
    return apiClient.delete(`/devices/${deviceId}/schedule/slots/${slotId}`);
  },
  addScheduleException(deviceId, data) {
    return apiClient.post(`/devices/${deviceId}/schedule/exceptions`, data);
  },
  deleteScheduleException(deviceId, exceptionId) {
    return apiClient.delete(`/devices/${deviceId}/schedule/exceptions/${exceptionId}`);
  },
  sendScheduleToDevice(deviceId) {
    return apiClient.post(`/devices/${deviceId}/schedule/send`);
  },
  previewSchedule(deviceId) {
    return apiClient.get(`/devices/${deviceId}/schedule/preview`);
  },

  // ---- Firmware OTA (admin) ----
  getFirmwares() {
    return apiClient.get('/admin/firmwares');
  },
  uploadFirmware(formData) {
    return apiClient.post('/admin/firmwares', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000
    });
  },
  deleteFirmware(id) {
    return apiClient.delete(`/admin/firmwares/${id}`);
  },
  getOtaCampaigns() {
    return apiClient.get('/admin/ota/campaigns');
  },
  createOtaCampaign(deviceId, targetVersion, force = false) {
    return apiClient.post('/admin/ota/campaigns', { deviceId, target_version: targetVersion, force });
  },
  // Archivage d'une version et builds de banc (06/10/2026).
  archiverFirmware(version, archiver) { return apiClient.post(`/admin/firmwares/${version}/${archiver ? 'archiver' : 'desarchiver'}`); },
  marquerBanc(version, banc) { return apiClient.put(`/admin/firmwares/${version}/banc`, { banc }); },
  cancelOtaCampaign(deviceId) {
    return apiClient.delete(`/admin/ota/campaigns/${deviceId}`);
  },
  // Installation imposée (06/10/2026) : `confirme` si le client est en manuel, `urgence` = même armé.
  installOtaCampaign(deviceId, { confirme = false, urgence = false } = {}) {
    return apiClient.post(`/admin/ota/campaigns/${deviceId}/install`, { confirme, urgence });
  },
  // Données mobiles des SIM 1NCE (06/10/2026).
  simsParc() { return apiClient.get('/admin/sims'); },
  simsJours(iccid, jours) { return apiClient.get(`/admin/sims/${encodeURIComponent(iccid)}/jours`, { params: { jours } }); },
  simsSessions(iccid, n) { return apiClient.get(`/admin/sims/${encodeURIComponent(iccid)}/sessions`, { params: { n } }); },
  simsCouts(cartes, depuis, jusqua) { return apiClient.get('/admin/sims/couts', { params: { ...(cartes ? { cartes } : {}), ...(depuis ? { depuis } : {}), ...(jusqua ? { jusqua } : {}) } }); },
  simsTrafic(ident, jours) { return apiClient.get(`/admin/sims/trafic/${encodeURIComponent(ident)}`, { params: { jours } }); },
  simsReleve() { return apiClient.post('/admin/sims/releve', null, { timeout: 60000 }); },
  // Déploiements par vagues (06/10/2026).
  listerDeploiements() { return apiClient.get('/admin/ota/rollouts'); },
  apercuDeploiement(p) { return apiClient.post('/admin/ota/rollouts/apercu', p); },
  creerDeploiement(p) { return apiClient.post('/admin/ota/rollouts', p); },
  actionDeploiement(id, action) { return apiClient.post(`/admin/ota/rollouts/${id}/${action}`); },
  setClientOtaAuto(clientId, enabled) {
    return apiClient.put(`/admin/ota/clients/${clientId}/auto`, { enabled });
  },

  // ---- Client ----
  getClientDashboard() {
    return apiClient.get('/clients/me');
  },
  // Recherche dans l'historique de ses propres cartes (cloisonné côté serveur)
  searchClientEvents(params) {
    return apiClient.get('/clients/me/events', { params });
  },
  getClientEventFacets() {
    return apiClient.get('/clients/me/events/facets');
  },
  // Le client lance l'installation d'une maj prête sur son device (mode manuel)
  // « J'ai vu » : l'alarme en attente ne met plus l'appareil en rouge, pour tout le compte (B11).
  marquerAlarmeVue(deviceId) {
    return apiClient.post(`/devices/${deviceId}/alarm/seen`);
  },
  installClientOta(deviceId) {
    return apiClient.post(`/clients/devices/${deviceId}/ota/install`);
  },
  // Le client règle sa préférence « mise à jour automatique »
  setClientOtaAutoPref(enabled) {
    return apiClient.put('/clients/me/ota-auto', { enabled });
  }
};
